import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Shirt, Upload } from 'lucide-react';
import SignedImage from '@/components/ipe/SignedImage';
import { IPE_UPLOAD_ACCEPT, IPE_UPLOAD_FORMAT_HINT } from '@/utils/ipeImageUploadFormats';
import { processImageUpload } from '@/utils/imageUpload';
import { imageStorageAPI } from '@/utils/api';
import { resolveUploadPreviewUrl } from '@/utils/imageUrlUtils';
import { showToast } from '@/utils/toast';
import { ParamRow } from './WorkflowParamFields';
import { buildTryOnUploadedModelSelectionParams } from '@/pages/ipe/workbench/utils/tryOnSystemModelPresetFallback';

export default function WorkflowModelImageUpload({
  modelImageUrl = '',
  modelImagePreviewUrl = '',
  usedUploadedModel = false,
  disabled = false,
  onChange,
}) {
  const { t } = useTranslation('workbench');
  const fileRef = useRef(null);
  const dragCounterRef = useRef(0);
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const hasImage = usedUploadedModel === true && Boolean(String(modelImageUrl || '').trim());
  const dropzoneBusy = uploading;

  const clearImage = useCallback(() => {
    onChange({
      usedUploadedModel: false,
      modelImageUrl: '',
      modelImagePreviewUrl: '',
    });
  }, [onChange]);

  const applyUpload = useCallback(async (file) => {
    if (!file?.type?.startsWith('image/') || disabled || uploading) return;
    setUploading(true);
    try {
      const item = await processImageUpload(file);
      let url = item?.url || item?.imageUrl || item?.serverUrl;
      let uploadResult = null;
      if (!url && item?.dataUrl) {
        uploadResult = await imageStorageAPI.save(item.dataUrl, { folder: 'image-space' });
        url = uploadResult?.url || uploadResult?.imageUrl;
      }
      if (!url) {
        throw new Error(t('workflowModelUpload.uploadServerMissing'));
      }
      const trimmed = String(url).trim();
      const previewUrl = await resolveUploadPreviewUrl(trimmed, uploadResult);
      onChange(buildTryOnUploadedModelSelectionParams({
        modelImageUrl: trimmed,
        modelImagePreviewUrl: previewUrl || trimmed,
      }));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [disabled, onChange, uploading, t]);

  const onFiles = useCallback(async (fileList) => {
    const file = Array.from(fileList || []).find((entry) => entry?.type?.startsWith('image/'));
    if (!file) return;
    try {
      await applyUpload(file);
    } catch (error) {
      showToast(error?.message || t('workflowModelUpload.uploadFailed'), 'error');
    }
  }, [applyUpload, t]);

  const triggerFileSelect = () => {
    if (disabled || dropzoneBusy) return;
    fileRef.current?.click();
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current += 1;
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDragOver(false);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    if (disabled || dropzoneBusy) return;
    onFiles(event.dataTransfer?.files);
  };

  return (
    <ParamRow
      label={t('workflowModelUpload.customModelImage')}
      hint={t('workflowModelUpload.pauseHint')}
      stacked
    >
      <div className="model-studio__toolbar-upload">
        <input
          ref={fileRef}
          type="file"
          accept={IPE_UPLOAD_ACCEPT}
          onChange={(event) => onFiles(event.target.files)}
          className="img2img-studio__file-input"
          disabled={disabled || dropzoneBusy}
          tabIndex={-1}
        />
        <button
          type="button"
          className={`model-studio__dropzone${isDragOver ? ' is-dragover' : ''}${hasImage ? ' is-filled' : ''}${dropzoneBusy ? ' is-busy' : ''}`}
          onClick={triggerFileSelect}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          disabled={disabled || dropzoneBusy}
          aria-label={t('workflowModelUpload.uploadModelAria')}
        >
          {hasImage ? (
            <>
              <div className="model-studio__dropzone-preview">
                <SignedImage
                  url={modelImageUrl}
                  displayUrl={modelImagePreviewUrl || modelImageUrl}
                  alt={t('workflowModelUpload.customModelImage')}
                />
              </div>
              <div className="model-studio__dropzone-filled-body">
                <p className="model-studio__dropzone-title">{t('studioChrome.modelUploaded')}</p>
                <p className="model-studio__dropzone-hint">{t('workflowModelUpload.replaceHint')}</p>
                <div className="model-studio__dropzone-filled-actions">
                  <button
                    type="button"
                    className="model-studio__dropzone-link"
                    onClick={(event) => {
                      event.stopPropagation();
                      triggerFileSelect();
                    }}
                    disabled={disabled || dropzoneBusy}
                  >
                    {t('studioChrome.reupload')}
                  </button>
                  <button
                    type="button"
                    className="model-studio__dropzone-link model-studio__dropzone-link--danger"
                    onClick={(event) => {
                      event.stopPropagation();
                      clearImage();
                    }}
                    disabled={disabled}
                  >
                    {t('studioChrome.remove')}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="model-studio__dropzone-icon-wrap" aria-hidden="true">
                <Shirt size={42} strokeWidth={1.4} className="model-studio__dropzone-icon" />
                <span className="model-studio__dropzone-badge">
                  <Upload size={14} strokeWidth={2.2} />
                </span>
              </div>
              <p className="model-studio__dropzone-title">{t('studioChrome.uploadModelOptional')}</p>
              <p className="model-studio__dropzone-hint">
                {t('studioChrome.modelSlotHint')}
                <br />
                {IPE_UPLOAD_FORMAT_HINT}
              </p>
              {dropzoneBusy ? (
                <p className="model-studio__dropzone-status">
                  <Loader2 size={14} className="img2img-studio__spin" aria-hidden="true" />
                  {t('studioChrome.uploading')}
                </p>
              ) : null}
            </>
          )}
        </button>
      </div>
    </ParamRow>
  );
}
